import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import ExperienceSummaryBadge from '../components/ExperienceSummaryBadge';
import ConfidenceBadge from '../components/ConfidenceBadge';
import StarRating from '../components/StarRating';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { 
    MapPin, 
    FileText, 
    Plus, 
    Loader2, 
    ArrowLeft,
    Calendar,
    CheckCircle,
    XCircle,
    AlertTriangle,
    Flag,
    ShieldCheck,
    BarChart3,
    HelpCircle
} from 'lucide-react';
import { addressAPI, reviewAPI } from '../lib/api';
import { formatDate, getProjectSizeLabel } from '../lib/utils';
import { toast } from 'sonner';

/**
 * Data Status Badge - Shows review count and summary availability
 * LEGAL COMPLIANCE: Does NOT display risk terminology
 * Summary available when at least 1 review exists
 */
const DataStatusBadge = ({ reviewCount = 0, size = 'default' }) => {
    const hasData = reviewCount >= 1;
    
    const sizeClasses = {
        small: 'px-2 py-1 text-xs',
        default: 'px-3 py-1.5 text-sm',
        large: 'px-4 py-2 text-base'
    };
    
    if (hasData) {
        return (
            <span className={`inline-flex items-center gap-1.5 font-medium rounded-lg bg-primary/20 text-primary border border-primary/30 ${sizeClasses[size]}`}>
                <BarChart3 className="h-3.5 w-3.5" />
                Summary Available
            </span>
        );
    }
    
    return (
        <span className={`inline-flex items-center gap-1.5 font-medium rounded-lg bg-muted/50 text-muted-foreground border border-border ${sizeClasses[size]}`}>
            <HelpCircle className="h-3.5 w-3.5" />
            No Contractor Data Yet
        </span>
    );
};

const AddressProfile = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [address, setAddress] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadAddressData();
    }, [id]);

    const loadAddressData = async () => {
        try {
            const [addressRes, reviewsRes] = await Promise.all([
                addressAPI.get(id),
                addressAPI.getReviews(id)
            ]);
            setAddress(addressRes.data);
            setReviews(reviewsRes.data);
        } catch (err) {
            toast.error('Failed to load address data');
            navigate('/dashboard');
        } finally {
            setLoading(false);
        }
    };

    const handleFlagReview = async (reviewId) => {
        try {
            await reviewAPI.flag(reviewId, 'Flagged by user');
            toast.success('Review flagged for moderation');
            loadAddressData();
        } catch (err) {
            toast.error('Failed to flag review');
        }
    };

    const ratingLabels = {
        payment_timeliness: 'My payment experience',
        communication: 'My communication experience',
        professionalism: 'My professionalism experience',
        schedule_reliability: 'My schedule experience',
        change_order_fairness: 'My scope change experience',
        jobsite_access: 'My site access experience'
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex justify-center items-center min-h-[60vh]">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
                {/* Back Button */}
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(-1)}
                    className="mb-4 -ml-2"
                    data-testid="back-btn"
                >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Back
                </Button>

                {/* Address Header - Compact */}
                <div className="mb-4 sm:mb-6">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                        <div className="flex items-start gap-3">
                            <div className="p-2.5 rounded-lg bg-primary/10 flex-shrink-0">
                                <MapPin className="h-6 w-6 text-primary" />
                            </div>
                            <div className="min-w-0">
                                <h1 className="font-bold text-xl sm:text-2xl text-foreground truncate">
                                    {address?.street_address}
                                </h1>
                                <p className="text-sm text-muted-foreground">
                                    {address?.city}{address?.state && `, ${address?.state}`} {address?.zip_code}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                            <div className="flex flex-col gap-1 items-end">
                                <DataStatusBadge reviewCount={address?.review_count || 0} size="large" />
                                <ConfidenceBadge score={address?.confidence_score} size="small" />
                            </div>
                            <Link to={`/review/new/${id}`}>
                                <Button 
                                    size="sm"
                                    className="active:scale-95 transition-transform"
                                    data-testid="submit-review-btn"
                                >
                                    <Plus className="h-4 w-4 mr-1" />
                                    Review
                                </Button>
                            </Link>
                        </div>
                    </div>

                    {address?.ownership_reset_at && (
                        <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                            <p className="text-amber-500 text-xs flex items-center gap-2">
                                <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
                                Ownership reset {formatDate(address.ownership_reset_at)} - only newer reviews shown
                            </p>
                        </div>
                    )}
                </div>

                {/* Data Status & Confidence Summary Card */}
                <Card className="bg-card border-border mb-4">
                    <CardContent className="p-4">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div className="flex items-center gap-4">
                                <div>
                                    <p className="text-xs text-muted-foreground mb-1">Experience Summary</p>
                                    <DataStatusBadge reviewCount={address?.review_count || 0} size="default" />
                                </div>
                                <div className="h-8 w-px bg-border" />
                                <div>
                                    <p className="text-xs text-muted-foreground mb-1">Data Confidence</p>
                                    <ConfidenceBadge score={address?.confidence_score} size="default" />
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="text-xs text-muted-foreground">
                                    Based on {address?.review_count || 0} review{(address?.review_count || 0) !== 1 ? 's' : ''}
                                </p>
                                {address?.confidence_score < 70 && (
                                    <p className="text-xs text-amber-500 mt-1">
                                        More reviews improve confidence
                                    </p>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4">
                    <Card className="bg-card border-border">
                        <CardContent className="p-3 sm:p-4 text-center">
                            <p className="text-2xl sm:text-3xl font-bold text-foreground tabular-nums">
                                {address?.review_count || 0}
                            </p>
                            <p className="text-xs text-muted-foreground">Reviews</p>
                        </CardContent>
                    </Card>
                    
                    {address?.avg_ratings && Object.keys(address.avg_ratings).length > 0 ? (
                        <>
                            <Card className="bg-card border-border">
                                <CardContent className="p-3 sm:p-4 text-center">
                                    <p className="text-2xl sm:text-3xl font-bold text-foreground tabular-nums">
                                        {(Object.values(address.avg_ratings).reduce((a, b) => a + b, 0) / 
                                          Object.values(address.avg_ratings).length).toFixed(1)}
                                    </p>
                                    <p className="text-xs text-muted-foreground">Avg Rating</p>
                                </CardContent>
                            </Card>
                            <Card className="bg-card border-border">
                                <CardContent className="p-3 sm:p-4 text-center">
                                    <p className="text-2xl sm:text-3xl font-bold text-foreground tabular-nums">
                                        {address.avg_ratings.payment_timeliness?.toFixed(1) || '-'}
                                    </p>
                                    <p className="text-xs text-muted-foreground">Payment</p>
                                </CardContent>
                            </Card>
                        </>
                    ) : (
                        <>
                            <Card className="bg-card border-border">
                                <CardContent className="p-3 sm:p-4 text-center">
                                    <p className="text-2xl sm:text-3xl font-bold text-muted-foreground">-</p>
                                    <p className="text-xs text-muted-foreground">Avg Rating</p>
                                </CardContent>
                            </Card>
                            <Card className="bg-card border-border">
                                <CardContent className="p-3 sm:p-4 text-center">
                                    <p className="text-2xl sm:text-3xl font-bold text-muted-foreground">-</p>
                                    <p className="text-xs text-muted-foreground">Payment</p>
                                </CardContent>
                            </Card>
                        </>
                    )}
                </div>

                {/* Average Ratings - Compact */}
                {address?.avg_ratings && Object.keys(address.avg_ratings).length > 0 && (
                    <Card className="bg-card border-border mb-4">
                        <CardContent className="p-3 sm:p-4">
                            <h3 className="font-medium text-sm text-foreground mb-3">Rating Breakdown</h3>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                {Object.entries(address.avg_ratings).map(([key, value]) => (
                                    <div key={key} className="flex items-center justify-between p-2 bg-accent/40 rounded-lg">
                                        <span className="text-xs text-muted-foreground truncate mr-2">
                                            {ratingLabels[key] || key}
                                        </span>
                                        <StarRating rating={value} readOnly size="small" />
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Reviews List */}
                <Card className="bg-card border-border">
                    <CardContent className="p-3 sm:p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <FileText className="h-4 w-4 text-primary" />
                            <span className="font-medium text-sm text-foreground">Reviews ({reviews.length})</span>
                        </div>
                        {reviews.length > 0 ? (
                            <div className="space-y-4">
                                {reviews.map((review) => (
                                    <div 
                                        key={review.id} 
                                        className="p-4 bg-accent/30 rounded-lg border border-border"
                                        data-testid={`review-${review.id}`}
                                    >
                                        {/* Review Header */}
                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                                            <div>
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <p className="font-medium text-foreground">
                                                        {review.contractor_company}
                                                    </p>
                                                    {review.has_verified_evidence && (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 text-xs font-medium rounded-full">
                                                            <ShieldCheck className="h-3 w-3" />
                                                            Verified Evidence
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-sm text-muted-foreground">
                                                    {review.job_type} • {getProjectSizeLabel(review.project_size)}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                <Calendar className="h-4 w-4" />
                                                {formatDate(review.created_at)}
                                            </div>
                                        </div>

                                        {/* Job Details */}
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                                            <div className="flex items-center gap-2">
                                                {review.contract_signed ? (
                                                    <CheckCircle className="h-4 w-4 text-emerald-500" />
                                                ) : (
                                                    <XCircle className="h-4 w-4 text-red-500" />
                                                )}
                                                <span className="text-sm text-muted-foreground">
                                                    Contract Signed
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {review.payment_received ? (
                                                    <CheckCircle className="h-4 w-4 text-emerald-500" />
                                                ) : (
                                                    <XCircle className="h-4 w-4 text-red-500" />
                                                )}
                                                <span className="text-sm text-muted-foreground">
                                                    Payment Received
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {review.would_work_again ? (
                                                    <CheckCircle className="h-4 w-4 text-emerald-500" />
                                                ) : (
                                                    <XCircle className="h-4 w-4 text-red-500" />
                                                )}
                                                <span className="text-sm text-muted-foreground">
                                                    Would Work Again
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {review.decline_future_work ? (
                                                    <XCircle className="h-4 w-4 text-red-500" />
                                                ) : (
                                                    <CheckCircle className="h-4 w-4 text-emerald-500" />
                                                )}
                                                <span className="text-sm text-muted-foreground">
                                                    {review.decline_future_work ? 'Declined Future' : 'Open to Future'}
                                                </span>
                                            </div>
                                        </div>

                                        <Separator className="my-4" />

                                        {/* Ratings */}
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                                            {Object.entries(review.ratings).map(([key, value]) => (
                                                <div key={key} className="flex items-center justify-between">
                                                    <span className="text-xs text-muted-foreground">
                                                        {ratingLabels[key] || key}
                                                    </span>
                                                    <StarRating rating={value} readOnly size="small" />
                                                </div>
                                            ))}
                                        </div>

                                        {/* Notes */}
                                        {review.notes && (
                                            <div className="mt-4 p-3 bg-accent/50 rounded-lg">
                                                <p className="text-sm text-muted-foreground">{review.notes}</p>
                                            </div>
                                        )}

                                        {/* Flag Button */}
                                        <div className="mt-4 flex justify-end">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleFlagReview(review.id)}
                                                className="text-muted-foreground hover:text-red-400"
                                                data-testid={`flag-review-${review.id}`}
                                            >
                                                <Flag className="h-4 w-4 mr-1" />
                                                Flag Review
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                                <p className="text-muted-foreground mb-4">
                                    No reviews yet for this address
                                </p>
                                <Link to={`/review/new/${id}`}>
                                    <Button data-testid="first-review-btn">
                                        <Plus className="h-4 w-4 mr-2" />
                                        Be the first to review
                                    </Button>
                                </Link>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
};

export default AddressProfile;
